import React, { useState, useEffect, useCallback } from 'react';
import { Plus, RefreshCw, Trash2 } from 'lucide-react';
import { riskRegisterApi, Risk, RiskHeatmapCell } from '../api/riskRegister.api';
import { useAuth } from '@contexts/AuthContext';
import ConfirmationModal from '@/components/ConfirmationModal';

const CATEGORIES = ['operational', 'financial', 'legal', 'reputational', 'strategic', 'compliance'];
const TREATMENTS = ['accept', 'mitigate', 'transfer', 'avoid'];


const getRiskBadge = (score: number) => {
  if (score >= 15) return 'bg-red-100 text-red-800';
  if (score >= 10) return 'bg-orange-100 text-orange-800';
  if (score >= 5) return 'bg-yellow-100 text-yellow-800';
  return 'bg-green-100 text-green-800';
};

const getHeatColor = (score: number) => {
  if (score >= 20) return 'bg-red-600';
  if (score >= 15) return 'bg-red-400';
  if (score >= 10) return 'bg-orange-400';
  if (score >= 5) return 'bg-yellow-300';
  return 'bg-green-200';
};

const RiskRegister: React.FC = () => {
  const { user } = useAuth();
  const [view, setView] = useState<'list' | 'heatmap'>('list');
  const [risks, setRisks] = useState<Risk[]>([]);
  const [heatmap, setHeatmap] = useState<RiskHeatmapCell[]>([]);
  const [stats, setStats] = useState<{ total: number; critical: number; high: number; medium: number; low: number } | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [createModal, setCreateModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'operational', likelihood: 3, impact: 3, treatment: 'mitigate' });
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; riskId: number | null; riskTitle: string }>({ open: false, riskId: null, riskTitle: '' });
  const [deleteLoading, setDeleteLoading] = useState(false);

  const companyId = user?.company_id;

  const load = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError('');
    try {
      const [list, statsRes, hmap] = await Promise.all([
        riskRegisterApi.list({ ...(statusFilter ? { status: statusFilter } : {}), ...(categoryFilter ? { category: categoryFilter } : {}), company_id: companyId }),
        riskRegisterApi.getStats(companyId),
        riskRegisterApi.getHeatmap(companyId),
      ]);
      setRisks((list as any).risks || []);
      setStats(statsRes);
      setHeatmap(Array.isArray(hmap) ? hmap : []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, categoryFilter, companyId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!form.title.trim() || !companyId) return;
    setSubmitting(true);
    try {
      await riskRegisterApi.create({ company_id: companyId, ...form as any });
      setCreateModal(false);
      setForm({ title: '', description: '', category: 'operational', likelihood: 3, impact: 3, treatment: 'mitigate' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteRisk = async () => {
    if (!deleteConfirm.riskId) return;
    setDeleteLoading(true);
    try {
      await riskRegisterApi.delete(deleteConfirm.riskId);
      setDeleteConfirm({ open: false, riskId: null, riskTitle: '' });
      load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  // Build 5x5 heatmap grid
  const heatmapGrid: Record<string, number> = {};
  heatmap.forEach(cell => { heatmapGrid[`${cell.likelihood}-${cell.impact}`] = cell.count; });

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Risk Register</h1>
        <div className="flex gap-2">
          <div className="flex border rounded-lg overflow-hidden">
            <button onClick={() => setView('list')} className={`px-3 py-2 text-sm ${view === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>List</button>
            <button onClick={() => setView('heatmap')} className={`px-3 py-2 text-sm ${view === 'heatmap' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>Heatmap</button>
          </div>
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">
            <RefreshCw size={16} />
          </button>
          <button onClick={() => setCreateModal(true)} className="flex items-center gap-1 px-3 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700">
            <Plus size={16} /> Add Risk
          </button>
        </div>
      </div>

      {error && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>}

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {[
            { label: 'Total', value: stats.total, color: 'text-gray-900' },
            { label: 'Critical (15+)', value: stats.critical, color: 'text-red-600' },
            { label: 'High (10-14)', value: stats.high, color: 'text-orange-600' },
            { label: 'Medium (5-9)', value: stats.medium, color: 'text-yellow-600' },
            { label: 'Low (1-4)', value: stats.low, color: 'text-green-600' },
          ].map(({ label, value, color }) => (
            <div key={label} className="bg-white border rounded-xl p-4">
              <p className="text-xs text-gray-500 mb-1">{label}</p>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
            </div>
          ))}
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" /></div>
      ) : view === 'list' ? (
        <>
          <div className="flex gap-3">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              {['', 'open', 'mitigated', 'closed'].map(s => <option key={s} value={s}>{s || 'All Statuses'}</option>)}
            </select>
            <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
              {['', ...CATEGORIES].map(c => <option key={c} value={c}>{c || 'All Categories'}</option>)}
            </select>
          </div>
          <div className="bg-white border rounded-xl overflow-hidden">
            {risks.length === 0 ? <div className="py-12 text-gray-500">No risks recorded.</div> : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>{['Title', 'Category', 'L', 'I', 'Score', 'Treatment', 'Status', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                  ))}</tr>
                </thead>
                <tbody className="divide-y">
                  {risks.map(r => (
                    <tr key={r.id}>
                      <td className="px-4 py-3 text-left font-medium text-gray-900">{r.title}</td>
                      <td className="px-4 py-3 text-left capitalize text-gray-600">{r.category}</td>
                      <td className="px-4 py-3 text-left">{r.likelihood}</td>
                      <td className="px-4 py-3 text-left">{r.impact}</td>
                      <td className="px-4 py-3 text-left">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${getRiskBadge(r.risk_score)}`}>{r.risk_score}</span>
                      </td>
                      <td className="px-4 py-3 text-left capitalize text-gray-600">{r.treatment}</td>
                      <td className="px-4 py-3 text-left">
                        <span className={`px-2 py-0.5 rounded-full text-xs ${r.status === 'open' ? 'bg-red-100 text-red-800' : r.status === 'mitigated' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-left">
                        <button
                          onClick={() => setDeleteConfirm({ open: true, riskId: r.id, riskTitle: r.title })}
                          className="text-red-500 hover:text-red-700 p-1"
                          title="Delete risk"
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <div className="bg-white border rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-700 mb-4">Risk Heatmap (Likelihood × Impact)</h3>
          <div className="overflow-auto">
            <table className="text-center text-xs border-collapse">
              <thead>
                <tr>
                  <th className="p-2 text-gray-500">L\I</th>
                  {[1, 2, 3, 4, 5].map(i => <th key={i} className="p-2 w-16 text-gray-500">Impact {i}</th>)}
                </tr>
              </thead>
              <tbody>
                {[5, 4, 3, 2, 1].map(l => (
                  <tr key={l}>
                    <td className="p-2 text-left text-gray-500 font-medium">L{l}</td>
                    {[1, 2, 3, 4, 5].map(i => {
                      const score = l * i;
                      const count = heatmapGrid[`${l}-${i}`] || 0;
                      return (
                        <td key={i} className={`p-2 w-16 h-12 ${getHeatColor(score)} rounded text-xs font-semibold`}>
                          {count > 0 ? count : ''}
                          <div className="text-[10px] opacity-70">{score}</div>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="flex gap-4 mt-4 text-xs text-gray-500">
              {[{ label: 'Low (1-4)', cls: 'bg-green-200' }, { label: 'Medium (5-9)', cls: 'bg-yellow-300' }, { label: 'High (10-14)', cls: 'bg-orange-400' }, { label: 'Critical (15+)', cls: 'bg-red-400' }].map(({ label, cls }) => (
                <div key={label} className="flex items-center gap-1"><span className={`w-4 h-4 rounded ${cls}`} />{label}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={deleteConfirm.open}
        onClose={() => setDeleteConfirm({ open: false, riskId: null, riskTitle: '' })}
        onConfirm={confirmDeleteRisk}
        title="Delete Risk"
        message={`Are you sure you want to delete "${deleteConfirm.riskTitle}"? This action cannot be undone.`}
        confirmText="Delete Risk"
        cancelText="Cancel"
        type="danger"
        loading={deleteLoading}
      />

      {createModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-lg space-y-4">
            <h2 className="text-lg font-semibold">Add Risk</h2>
            <input placeholder="Title *" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" />
            <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 text-sm" rows={2} />
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-gray-500 block mb-1">Category</label>
                <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm">
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Treatment</label>
                <select value={form.treatment} onChange={e => setForm({ ...form, treatment: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm">
                  {TREATMENTS.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Likelihood (1-5): <strong>{form.likelihood}</strong></label>
                <input type="range" min={1} max={5} value={form.likelihood} onChange={e => setForm({ ...form, likelihood: Number(e.target.value) })} className="w-full" />
              </div>
              <div>
                <label className="text-xs text-gray-500 block mb-1">Impact (1-5): <strong>{form.impact}</strong></label>
                <input type="range" min={1} max={5} value={form.impact} onChange={e => setForm({ ...form, impact: Number(e.target.value) })} className="w-full" />
              </div>
            </div>
            <p className="text-sm">Risk Score: <span className={`px-2 py-0.5 rounded font-bold ${getRiskBadge(form.likelihood * form.impact)}`}>{form.likelihood * form.impact}</span></p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setCreateModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={submitting || !form.title.trim()}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {submitting ? 'Saving...' : 'Add Risk'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskRegister;
